import { DialogType, TabAndPanelContents, TabListAlignment, TabsProps, TabStyles, TabsWidget } from '@tableau/widgets';
import React from 'react';

import { ReactModalOptions } from '~commonInfra/dialogs/ReactModalService';
import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/common/react/CommonStyles';
import { FlowBaseInfo } from '~src/flow/models/FlowInfo';
import { RunFlowTaskInfo } from '~src/flow/models/RunFlowTaskInfo';
import { LinkedTaskDialogStateContextProvider } from '~src/linkedTasks/components/LinkedTaskDialogStateContextProvider';
import { LinkedTasksDialogContent } from '~src/linkedTasks/components/LinkedTasksDialogContent';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';
import { cssClass } from '~src/style';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { RunFlowTaskDialogContent } from './RunFlowTaskDialogContent';

const rootClass = 'FlowTaskDialog';

const outerSpacing = cssClass(rootClass, 'outerSpacing', {
  paddingTop: 8
});

const outerMargin = cssClass(rootClass, 'outerMargin', {
  display: 'flex',
  flexDirection: 'column',
  margin: `${CommonStyles.Sizes.LargeSpace}px`,
});

const dimensionOverrides = {
  tabHeight: SpaceContainerStyles.SpaceTabHeight,
  tabHorizontalPadding: TabStyles.Spacing.LargeSpace,
  tabPanelPadding: '0',
};

const dialogBodyStyle = {
  padding: 0
};

const tabsStyle = cssClass(rootClass, 'tabs', {
  $nest: {
    'div[role=tablist]': {
      paddingLeft: 18,
      $nest: {
        '& > div[aria-selected=true]': {
          fontWeight: 500
        }
      }
    },
    'div[data-tb-test-id=flowTaskDialogId-tabpanel-container]': {
      display: 'flex'
    },
    'div[role=tabpanel]': {
      height: '100%',
      boxSizing: 'border-box',
      width: '100%'
    }
  }
});

export function getFlowTaskDialog(flowInfo: FlowBaseInfo, dismiss: () => void, outputId?: string, openLinkedTaskTab?: boolean): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.newTask(),
    dialogContent: <FlowTaskDialog flowInfo={flowInfo} dismiss={dismiss} outputId={outputId} openLinkedTaskTab={openLinkedTaskTab}/>,
    showCloseButton: true,
    dialogWidth: FeatureFlags.incrementalRefresh ? 900 : 600,
    dialogHeight: 525,
    shouldRecenterOnUpdate: true,
    shouldNotCloseOnGlassClick: true,
    dialogBodyStyle
  };
}

function getRunFlowTaskDialogContent(dismiss: () => void, taskInfo?: RunFlowTaskInfo, flowInfo?: FlowBaseInfo, singleSelectedOutputId?: string): React.ReactElement {
  return (
    <div className={outerMargin}>
      <RunFlowTaskDialogContent
        taskInfo={taskInfo ? taskInfo : undefined}
        flowInfo={flowInfo ? flowInfo : undefined}
        singleSelectedOutputId={singleSelectedOutputId ? singleSelectedOutputId : undefined}
        dismiss={dismiss}/>
    </div>
  );
}

export function editFlowRunSchedulesDialog(taskInfo: RunFlowTaskInfo, dismiss: () => void): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.editTask(),
    dialogContent: getRunFlowTaskDialogContent(dismiss, taskInfo, null, null),
    showCloseButton: true,
    dialogWidth: FeatureFlags.incrementalRefresh ? 900 : 600,
    shouldRecenterOnUpdate: true,
    shouldNotCloseOnGlassClick: true,
  };
}

export type FlowTaskDialogProps = {
  flowInfo: FlowBaseInfo;
  dismiss: () => void;
  outputId?: string;
  openLinkedTaskTab?: boolean;
}

export function FlowTaskDialog(props: FlowTaskDialogProps): JSX.Element {
  const tabs: TabAndPanelContents[] = [{
    tabContent: { label: Messages.scheduleSingleTask() },
    panelContent: getRunFlowTaskDialogContent(props.dismiss, null, props.flowInfo, props.outputId),
  }, {
    tabContent: { label: Messages.linkedTasks() },
    panelContent: <LinkedTasksDialogContent {...props}/>,

  }];
  const tabsProps: TabsProps = {
    anchorTabIndex: 0,
    ariaLabel: Messages.tasks(),
    initialActiveIndex: props.openLinkedTaskTab ? 1 : 0,
    tabListAlignment: TabListAlignment.Left,
    tabs: tabs,
    testId: 'flowTaskDialogId',
    dimensionOverrides
  };
  return (
    <LinkedTaskDialogStateContextProvider>
      <div className={outerSpacing}>
        <div className={SpaceContainerStyles.SpaceTabsBottom}/>
        <TabsWidget {...tabsProps} className={tabsStyle}/>
      </div>
    </LinkedTaskDialogStateContextProvider>
  );
}
